import React, { useMemo, useRef, useState } from 'react';

import { Layout } from 'antd';
import cn from 'classnames';
import throttle from 'lodash/throttle'
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';

import Search from '@components/CoinSearch';
import Icon from '@components/Icon';
import NavBar from '@components/Navbar';
import { BackTop, TabPane, Tabs } from '@element/index';
import useClient from '@lib/hooks/useClient';
import { useIsomorphicLayoutEffect } from '@lib/hooks/useIsomorphicLayoutEffect';
import { store } from '@store/index';
import I18n from '@utils/i18n';
import { OptionalList } from '@widget/market/OptionalList';
import { RiseFallCardList } from '@widget/market/RiseFallCardList';
import { SpotList } from '@widget/market/SpotList';
import { useZoneList } from '@widget/market/SpotList/hooks';
import HotCoin from '@widget/trade/HotCoin';

import styles from './index.module.less';

const { Header } = Layout;

const T = () => {
  const {
    marketStore: {
      hotSearch,
      fetchMarketCoin,
      fullMarketSymbols,
      fetchBaseCurrencies,
      fetchRecommendList
    },
    tradeStore: {
      fetchFavoriteList,
      favoriteObject: { zones }
      // favoriteObject
    },
    userStore: { isLogin }
    // withdrawalStore: { fetchAssetCoinList }
  } = store;
  const [activeKey, setActiveKey] = useState('1');
  const istop = useRef(false);
  const isPc = useClient();
  const router = useRouter();
  const { query } = router;
  const tabsEl = useRef(null); // 吸顶元素
  const initialOffsetTop = useRef<number>(); // 吸顶元素初始位置（top）

  const {
    currency,
    selectOptions,
    loading,
    dataList,
    zoneData,
    tabsChange,
    setCurrency,
    zone
  } = useZoneList(store);

  useIsomorphicLayoutEffect(() => {
    setActiveKey(query.activeKey as string || '1');
    async function fetchData () {
      const basePromiseArr = [
        // fetchFavoriteList(),
        fetchRecommendList(),
        hotSearch(),
        fetchMarketCoin(),
        fullMarketSymbols(),
        fetchBaseCurrencies()
      ];
      const promiseArr = isLogin
        ? [...basePromiseArr, fetchFavoriteList()]
        : basePromiseArr;
      await Promise.allSettled(promiseArr);
    }
    fetchData();
  }, []);

  const tabsJump = (activeKey) => {
    setActiveKey(activeKey);
    router.replace(`${router.pathname}?activeKey=${activeKey}`, undefined, {
      shallow: true
    });
  };

  useIsomorphicLayoutEffect(() => {
    setActiveKey(query.activeKey as string || '1');
  }, [router.query]);

  const zoneDetail = useMemo(() => {
    if (zone !== 'ALL') {
      const zoneItem = zoneData.find((i) => i.categoryKey === zone);
      return zoneItem;
    }
    return {};
  }, [zone, zoneData]);

  const updateTargetElClassName = ({ el, className, type }: {el: Element, className: string, type: 'add' | 'remove'}) => {
    el && el.classList[type](className)
  }

  const getTargetEl = (classNames: string[]) => {
    return classNames.map(i => document.getElementsByClassName(i)[0])
  }

  const onFixed = throttle(() => {
    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;
    initialOffsetTop.current =
      initialOffsetTop.current || tabsEl.current?.offsetTop; // 保存初始值
    const tabOffsetTop = initialOffsetTop.current;

    /**
     * navNode: 导航
     * firstOrderTab: 一级tab
     * zonesTab: 板块tabs
     * pairTrading: 交易对tabs
     */

    const [navNode, firstOrderTab, zonesTab, pairTrading] = getTargetEl(['navMarket', styles.tabsClass, 'zonesTab', 'pairTrading'])

    const updateElMap = [
      { el: firstOrderTab, className: styles.tabsClassAfter },
      { el: zonesTab, className: styles.zonesTabAfter },
      { el: pairTrading, className: styles.pairTradingAfter }]

    if (scrollTop >= tabOffsetTop && !istop.current) {
      istop.current = true;
      if (window.innerWidth > 960) {
        updateTargetElClassName({ el: navNode, className: styles.hideNav, type: 'add' })
      }
      // 添加className
      updateElMap.forEach((item) => {
        updateTargetElClassName({ ...item, type: 'add' })
      })
    }
    if (scrollTop < tabOffsetTop && istop.current) {
      istop.current = false;
      if (window.innerWidth > 960) {
        updateTargetElClassName({ el: navNode, className: styles.hideNav, type: 'remove' })
      }
      // 删除className
      updateElMap.forEach((item) => {
        updateTargetElClassName({ ...item, type: 'remove' })
      })
    }
  }, 16);

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('scroll', onFixed);
    return () => {
      window.removeEventListener('scroll', onFixed);
    };
  }, []);
  const headerInfo = useMemo(() => {
    const isZones = zoneDetail.categoryKey && activeKey === '1';
    /**
       * 板块：zoneDetail.categoryKey有值
       * 全部：zoneDetail.categoryKey无值
       * 自选：activeKey === "1"
       */
    return (
      <>
        <h1 className={cn(styles.title, 'PingFangSC-Semibold')}>
          {I18n.t(
            isZones
              ? `zoneName${zoneDetail.categoryKey.toUpperCase()}`
              : 'market'
          )}
        </h1>
        <p className={cn(styles.subTitle)}>
          {I18n.t(
            isZones
              ? `zoneDesc${zoneDetail.categoryKey.toUpperCase()}`
              : 'market_intro'
          )}
        </p>
        {isZones ? (
          <RiseFallCardList zoneDetail={zoneDetail} zoneDetailList={dataList} />
        ) : (
          <HotCoin />
        )}
      </>
    );
  }, [zoneDetail, dataList, activeKey]);

  return (
    <Layout>
      <Header>
        <div className="navMarket">
          <NavBar type={'2'} />
        </div>
      </Header>
      <article className={styles.contain}>
        {headerInfo}
        <div className={styles.card} ref={tabsEl}>
          {!isPc && <Search searchType="market" />}
          <Tabs
            className={styles.tabsClass}
            type="marketTab"
            tabBarExtraContent={isPc && <Search searchType="market" />}
            activeKey={activeKey}
            onChange={tabsJump}
            destroyInactiveTabPane
          >
            <TabPane
              tab={
                <h2 data-buried="web_market_markets_businesstype_select_favorites">
                  <Icon name="star" />
                  {I18n.t('wHMldYcm')}
                </h2>
              }
              key="0"
              type="marketTab"
            >
              <OptionalList zones={zones} tabsJump={tabsJump} />
            </TabPane>
            <TabPane
              tab={
                <h2 data-buried="web_market_markets_businesstype_select_spot">
                  {I18n.t('tOqCS6hi')}
                </h2>
              }
              key="1"
              type="marketTab"
            >
              <SpotList
                {...{
                  currency,
                  selectOptions,
                  loading,
                  dataList,
                  zoneData,
                  tabsChange,
                  setCurrency,
                  zone
                }}
              />
            </TabPane>
          </Tabs>
        </div>
      </article>
      <BackTop />
    </Layout>
  );
};

export default observer(T);
